import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SectionHeading from "@/components/feature/SectionHeading";
import MediaCard from "@/components/feature/MediaCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import NewsFlash from "@/pages/home/components/NewsFlash";
import { getMediaByType } from "@/lib/content";
import { topicList } from "@/mocks/topics";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Home() {
  usePageMeta({
    title: "po18官方网站 - 娱乐内容聚合平台",
    description:
      "po18为您提供最新最全的娱乐资讯、影视推荐及相关内容聚合，涵盖电影、电视剧和综艺节目，助您轻松发现优质娱乐资源。",
    keywords:
      "po18,娱乐,影视推荐,电影资讯,电视剧更新,综艺节目,娱乐资讯",
  });

  const movies = getMediaByType("movie").slice(0, 4);
  const tvs = getMediaByType("tv").slice(0, 4);
  const varieties = getMediaByType("variety").slice(0, 4);

  const sections = [
    {
      key: "movie",
      icon: "ri-film-line",
      title: "电影推荐",
      desc: "院线新片与口碑佳作，科幻、悬疑、爱情一网打尽。",
      moreTo: "/category/movie",
      items: movies,
    },
    {
      key: "tv",
      icon: "ri-tv-2-line",
      title: "电视剧推荐",
      desc: "都市、历史、悬疑、武侠，追踪热门剧集不错过。",
      moreTo: "/category/tv",
      items: tvs,
    },
    {
      key: "variety",
      icon: "ri-mic-line",
      title: "综艺推荐",
      desc: "音乐、旅行、美食、竞技，承包你的下饭好时光。",
      moreTo: "/category/variety",
      items: varieties,
    },
  ];

  return (
    <SiteLayout>
      <HeroCarousel />

      <div className="w-full px-4 md:px-6">
        {/* 快报 + 品牌简介 */}
        <section className="-mt-8 md:-mt-12 relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
          <div className="lg:col-span-2">
            <NewsFlash />
          </div>
          <div className="rounded-lg bg-primary-500 text-background-50 p-5 md:p-6 flex flex-col">
            <span className="w-10 h-10 rounded-md bg-background-50/20 flex items-center justify-center">
              <i className="ri-movie-2-line text-xl" />
            </span>
            <h3 className="mt-4 text-lg font-bold">po18 娱乐内容聚合平台</h3>
            <p className="mt-2 text-sm leading-relaxed text-background-50/90">
              聚合最新娱乐资讯与优质影视推荐，涵盖电影、电视剧与综艺节目。用清晰的分类与用心的推荐，帮你快速发现值得一看的好内容。
            </p>
            <div className="mt-auto pt-5 grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-xl font-black">36+</p>
                <p className="text-xs text-background-50/80 mt-0.5">影视条目</p>
              </div>
              <div>
                <p className="text-xl font-black">12+</p>
                <p className="text-xs text-background-50/80 mt-0.5">每日资讯</p>
              </div>
              <div>
                <p className="text-xl font-black">3</p>
                <p className="text-xs text-background-50/80 mt-0.5">精选专题</p>
              </div>
            </div>
          </div>
        </section>

        {/* 影视推荐网格 */}
        <div className="mt-12 md:mt-16 space-y-12 md:space-y-16">
          {sections.map((sec) => (
            <section key={sec.key}>
              <SectionHeading
                icon={sec.icon}
                title={sec.title}
                desc={sec.desc}
                moreTo={sec.moreTo}
                moreLabel={`更多${sec.title.replace("推荐", "")}`}
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {sec.items.map((item) => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* 专题合集 */}
        <section className="mt-12 md:mt-16">
          <SectionHeading
            icon="ri-bookmark-3-line"
            title="主题专题合集"
            desc="编辑精选的主题策划，带你按主题发现好内容。"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {topicList.map((topic) => (
              <Link
                key={topic.id}
                to={`/topic/${topic.id}`}
                className="group block cursor-pointer rounded-lg overflow-hidden bg-background-100 border border-background-200/70"
              >
                <div className="relative w-full aspect-[16/9] overflow-hidden bg-background-200">
                  <img
                    src={topic.image}
                    alt={`${topic.title} 专题`}
                    title={`${topic.title} - po18 精选专题`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 to-transparent" />
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold">
                    专题
                  </span>
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="text-base md:text-lg font-bold text-background-50 line-clamp-1">
                      {topic.title}
                    </h3>
                    <p className="text-xs text-background-100/90 truncate mt-0.5">
                      {topic.subtitle}
                    </p>
                  </div>
                </div>
                <div className="p-4 md:p-5">
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {topic.description}
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                    查看专题
                    <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* 品牌简介 */}
        <section className="mt-12 md:mt-16">
          <div className="rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-black text-foreground-950">
                  关于 po18
                </h2>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  po18 是一个面向大众的综合娱乐内容聚合平台，聚焦最新最全的娱乐资讯与影视推荐。
                  我们涵盖电影、电视剧、综艺节目三大内容类型，通过清晰的分类导航、真实完整的影视资料与用心的主题策划，
                  帮助每一位喜欢电影、电视剧和综艺的用户，快速获取优质娱乐内容，发现下一部值得看的作品。
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/category/movie"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-compass-3-line text-lg" />
                    开始探索
                  </Link>
                  <Link
                    to="/news"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-secondary-100 text-secondary-900 text-sm font-semibold hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-newspaper-line text-lg" />
                    浏览资讯
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: "ri-search-eye-line", t: "快速发现", d: "多维筛选与搜索，帮你快速定位想看的内容。" },
                  { icon: "ri-layout-grid-line", t: "清晰分类", d: "电影、电视剧、综艺分区明确，浏览高效。" },
                  { icon: "ri-heart-3-line", t: "本地收藏", d: "一键收藏感兴趣的作品，状态本地保存。" },
                  { icon: "ri-bookmark-3-line", t: "主题专题", d: "编辑策划的专题合集，按主题挑好剧。" },
                ].map((f) => (
                  <div
                    key={f.t}
                    className="rounded-lg bg-background-50 border border-background-200/70 p-4"
                  >
                    <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center">
                      <i className={`${f.icon} text-lg`} />
                    </span>
                    <h3 className="mt-3 text-sm font-bold text-foreground-900">
                      {f.t}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500 leading-relaxed">
                      {f.d}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}